import { ChangeEvent } from "react";
import { ToggleCheckbox as ToggleCheckboxComponent } from "views/components";
import {
  MEASUREMENT_UNIT_OPTIONS,
  SizeFormErrors,
  SizeFormState
} from "./teamTemplateSizeFormUtils";
import { MeasurementUnits } from "state/entities/teamTemplateSizes/teamTemplateSizesActions";
import style from "./style.module.css";

const ToggleCheckbox = ToggleCheckboxComponent as any;

interface TeamTemplateSizeFormProps {
  form: SizeFormState;
  errors: SizeFormErrors;
  onChange: (patch: Partial<SizeFormState>) => void;
  onFieldBlur: () => void;
}

const TeamTemplateSizeForm = ({
  form,
  errors,
  onChange,
  onFieldBlur
}: TeamTemplateSizeFormProps) => {
  const handleName = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ name: event.target.value });
  const handleWidth = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ width: event.target.value });
  const handleHeight = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ height: event.target.value });
  const handleUnits = (event: ChangeEvent<HTMLSelectElement>) =>
    onChange({ measurementUnits: event.target.value as MeasurementUnits });

  return (
    <div className={style.modalFields}>
      <div className={style.modalInputRow}>
        <label
          htmlFor="team-size-name"
          className={errors.name ? style.labelError : ""}
        >
          Name
        </label>
        <input
          id="team-size-name"
          name="name"
          type="text"
          className={style.textInput}
          placeholder="e.g. Storefront poster"
          value={form.name}
          onChange={handleName}
          onBlur={onFieldBlur}
          data-has-error={!!errors.name}
          data-testid="sizeNameInput"
        />
        {errors.name && <div className={style.errorText}>{errors.name}</div>}
      </div>

      <div className={style.dimensionsRow}>
        <div>
          <label
            htmlFor="team-size-width"
            className={errors.width ? style.labelError : ""}
          >
            Width
          </label>
          <input
            id="team-size-width"
            name="width"
            type="number"
            min={1}
            className={style.textInput}
            value={form.width}
            onChange={handleWidth}
            onBlur={onFieldBlur}
            data-has-error={!!errors.width}
            data-testid="sizeWidthInput"
          />
          {errors.width && (
            <div className={style.errorText}>{errors.width}</div>
          )}
        </div>

        <div>
          <label
            htmlFor="team-size-height"
            className={errors.height ? style.labelError : ""}
          >
            Height
          </label>
          <input
            id="team-size-height"
            name="height"
            type="number"
            min={1}
            className={style.textInput}
            value={form.height}
            onChange={handleHeight}
            onBlur={onFieldBlur}
            data-has-error={!!errors.height}
            data-testid="sizeHeightInput"
          />
          {errors.height && (
            <div className={style.errorText}>{errors.height}</div>
          )}
        </div>

        <div className={style.unitsColumn}>
          <label htmlFor="team-size-units">Units</label>
          <select
            id="team-size-units"
            className={style.selectInput}
            value={form.measurementUnits}
            onChange={handleUnits}
            data-testid="sizeUnitsSelect"
          >
            {MEASUREMENT_UNIT_OPTIONS.map(unit => (
              <option key={unit} value={unit}>
                {unit}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={style.toggleRow}>
        <div className={style.toggleLabel}>
          <span>Active</span>
          <span className={style.toggleDescription}>
            Inactive sizes are hidden from the catalogue picker.
          </span>
        </div>
        <ToggleCheckbox
          checked={form.active}
          onChange={(checked: boolean) => onChange({ active: checked })}
          width={40}
        />
      </div>

      <div className={style.toggleRow}>
        <div className={style.toggleLabel}>
          <span>Double-sided</span>
          <span className={style.toggleDescription}>
            Creates designs with a front and back page.
          </span>
        </div>
        <ToggleCheckbox
          checked={form.doubleSided}
          onChange={(checked: boolean) => onChange({ doubleSided: checked })}
          width={40}
        />
      </div>
    </div>
  );
};

export default TeamTemplateSizeForm;
